import * as React from "react";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cn } from "@/zapi-frontend/shared/lib/utils";

import { InnoveraMark } from "@libs/zapi-ui/ds/components/innovera-logo";
import { Separator } from "@libs/zapi-ui/ds/components/separator";
import { useScrolledPast } from "@/zapi-frontend/shared/hooks/useScrolledPast";

function AppHeader({
  surfaceOnScroll = false,
  className,
  ...props
}: React.ComponentProps<"header"> & { surfaceOnScroll?: boolean }) {
  const scrolled = useScrolledPast(4, surfaceOnScroll);
  return (
    <header
      data-slot="app-header"
      data-scrolled={scrolled || undefined}
      className={cn(
        "sticky top-0 z-35 transition-[background-color,box-shadow,backdrop-filter] duration-300 ease-out",
        "data-scrolled:bg-canvas/75 data-scrolled:shadow-[0_1px_0_var(--border)] data-scrolled:backdrop-blur-xl data-scrolled:backdrop-saturate-150",
        className
      )}
      {...props}
    />
  );
}

function AppHeaderContent({
  layout = "centered",
  className,
  ...props
}: React.ComponentProps<"div"> & { layout?: "centered" | "split" }) {
  return (
    <div
      data-slot="app-header-content"
      data-layout={layout}
      className={cn(
        "group/app-header mx-auto grid min-h-(--app-header-height) w-full max-w-screen-2xl grid-cols-[auto_1fr] items-center gap-x-3 gap-y-3 px-4 py-3 sm:px-6 lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:gap-x-6 xl:grid-cols-[1fr_minmax(0,40rem)_1fr]",
        "data-[layout=split]:grid-cols-[minmax(0,1fr)_auto] lg:data-[layout=split]:grid-cols-[minmax(0,1fr)_auto] xl:data-[layout=split]:grid-cols-[minmax(0,1fr)_auto]",
        className
      )}
      {...props}
    />
  );
}

function AppHeaderStart({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="app-header-start"
      className={cn("col-start-1 row-start-1 flex min-w-0 items-center gap-3", className)}
      {...props}
    />
  );
}

function AppHeaderCenter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="app-header-center"
      className={cn(
        "col-span-2 col-start-1 row-start-2 flex min-w-0 items-center gap-2 lg:col-span-1 lg:col-start-2 lg:row-start-1 lg:mx-auto lg:w-full lg:max-w-[40rem]",
        className
      )}
      {...props}
    />
  );
}

function AppHeaderEnd({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="app-header-end"
      className={cn(
        "col-start-2 row-start-1 flex min-w-0 items-center justify-end gap-1 sm:gap-2 lg:col-start-3 lg:group-data-[layout=split]/app-header:col-start-2",
        className
      )}
      {...props}
    />
  );
}

function AppHeaderBrand({ className, render, ...props }: useRender.ComponentProps<"a">) {
  return useRender({
    defaultTagName: "a",
    props: mergeProps<"a">(
      {
        className: cn(
          "flex size-10 shrink-0 items-center justify-center rounded-full text-greyscale-900 outline-none transition-colors hover:bg-accent focus-visible:ring-3 focus-visible:ring-ring/50 dark:text-greyscale-50",
          className
        ),
        "aria-label": "Innovera home",
        children: <InnoveraMark className="size-7" title="Innovera" />
      },
      props
    ),
    render,
    state: { slot: "app-header-brand" }
  });
}

function AppHeaderDivider({ className }: { className?: string }) {
  return (
    <Separator
      orientation="vertical"
      data-slot="app-header-divider"
      className={cn("data-vertical:h-6 data-vertical:self-center", className)}
    />
  );
}

export { AppHeader, AppHeaderContent, AppHeaderStart, AppHeaderCenter, AppHeaderEnd, AppHeaderBrand, AppHeaderDivider };
